import { gql, useQuery } from '@apollo/client';
import WellCountByYearChartThumbnail from '@assets/images/report-components/well-count-by-year-stacked-chart-thumbnail.png';
import { useEffect, useMemo, useState } from 'react';
import { ComponentBox } from '@/admins/SmartReports/components/Components/Common/ComponentBox';
import { ComponentTitle } from '@/admins/SmartReports/components/Components/Common/ComponentTitle';
import { LoadingErrorWrapper } from '@/admins/SmartReports/components/Components/Common/LoadingErrorWrapper';
import {
  type ComponentDefinition,
  type ComponentEditProps,
  ComponentViewMode,
  type ComponentViewProps,
  type UseDataType,
} from '@/admins/SmartReports/components/Components/types';
import { SmartReportComponentTypesEnum, SmartReportTypeEnum } from '@/admins/SmartReports/types';
import { useChartColors } from '@/hooks/useChartColors';
import {
  AssetAggregateByEnum,
  AssetAggregatesFieldsEnum,
  type AssetAggregatesStacked,
  AssetStackByEnum,
} from '@/types/GraphqlSchema';
import { GraphWrapper } from './GraphWrapper';
import { getLayout } from './helpers';

const QUERY = gql`
  query assetAggregatesStacked($input: AssetAggregatesInput!) {
    assetAggregatesStacked(input: $input) {
      data {
        type
        group
        values
      }
      stacks
    }
  }
`;

export const COMPONENT_ID = 'well_count_by_year_stacked_by_classification';

const useData = ({
  portfolio,
  persistedData,
  onComponentLoad,
}: UseDataType<AssetAggregatesStacked>) => {
  const [chartDataFetched, setChartDataFetched] = useState(persistedData);

  const { data, loading, error } = useQuery<{ assetAggregatesStacked: AssetAggregatesStacked }>(
    QUERY,
    {
      variables: {
        input: {
          by: AssetAggregateByEnum.WellCountYear,
          fields: [AssetAggregatesFieldsEnum.WellCountSum],
          stackBy: AssetStackByEnum.WellClassification,
          portfolio,
        },
      },
      skip: !!persistedData,
    },
  );

  useEffect(() => {
    if (data?.assetAggregatesStacked) {
      const chartData = data.assetAggregatesStacked;
      setChartDataFetched(chartData);
      if (onComponentLoad) {
        onComponentLoad({
          componentId: COMPONENT_ID,
          data: chartData,
        });
      }
    }
  }, [data, onComponentLoad]);

  return { chartData: chartDataFetched, error, loading };
};

const ComponentView = ({ data, mode }: ComponentViewProps<AssetAggregatesStacked>) => {
  const chartColors = useChartColors();

  const formattedData = useMemo(() => {
    try {
      if (!data || !data.data || !data.stacks) return null;

      const { data: chartData, stacks } = data;

      const formattedChartData = [...stacks].reverse().map((stackName, idx) => {
        const x = chartData.map(item => item.group); // Years
        const y = chartData.map(item => {
          const stackValues = item.values[stackName];
          return stackValues?.wellCountSum || 0;
        });

        return {
          x,
          y,
          type: 'bar',
          name: stackName,
          hovertemplate: '%{fullData.name}<br>Year: %{x}<br>Well Count: %{y:,.0f}<extra></extra>',
          marker: {
            color: chartColors[idx % chartColors.length],
          },
        };
      });

      return formattedChartData;
    } catch {
      return null;
    }
  }, [data, chartColors]);

  if (!formattedData) return null;

  return (
    <ComponentBox>
      <ComponentTitle>Project Well Count</ComponentTitle>
      <GraphWrapper
        data={formattedData}
        layout={getLayout({
          yaxis: { title: { text: '# of wells' } },
          barmode: 'stack',
          legend: { y: -0.2 },
        })}
        config={{
          staticPlot: mode !== ComponentViewMode.View,
        }}
      />
    </ComponentBox>
  );
};

const ComponentEdit = ({
  portfolio,
  persistedData,
  onComponentLoad,
}: ComponentEditProps<AssetAggregatesStacked>) => {
  const { chartData, loading, error } = useData({
    portfolio,
    persistedData,
    onComponentLoad,
  });

  return (
    <LoadingErrorWrapper loading={loading} error={error} data={chartData}>
      {chartData && <ComponentView data={chartData} mode={ComponentViewMode.Edit} />}
    </LoadingErrorWrapper>
  );
};

const ComponentThumbnail = () => (
  <ComponentBox>
    <ComponentTitle sx={{ fontSize: '0.25rem', marginBottom: 0 }}>
      Well Count by Year (Stacked by Classification)
    </ComponentTitle>
    <img
      src={WellCountByYearChartThumbnail}
      alt='Well Count By Year Stacked By Classification Chart Thumbnail'
      style={{ width: '100%', height: 'auto' }}
    />
  </ComponentBox>
);

export const WellCountByYearStackedByClassification: ComponentDefinition<AssetAggregatesStacked> = {
  id: COMPONENT_ID,
  name: 'Well Count By Year',
  type: SmartReportComponentTypesEnum.Chart,
  reportTypes: [SmartReportTypeEnum.Asset],
  Thumbnail: ComponentThumbnail,
  Component: ComponentEdit,
  ComponentView,
};
